import {
  ColumnDef,
  OnChangeFn,
  RowSelectionState,
} from "@tanstack/react-table";
import { useMemo } from "react";
import { SimpleTable } from "@/core/components/simple-table";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { sortDateDescending } from "@/core/utils/date";
import {
  EventCol,
  SpiffTypeCol,
  FlatTechnicianCol,
  SpiffPaymentAmountCol,
  EventDateCol,
  SelectionWithUndismissCol,
} from "@/spiffs/common/spiff-payments-table";
import { SPIFF_SIDEBAR_SECTIONS } from "@/spiffs/constants";
import { useSpiffSidebar } from "@/spiffs/context/spiff-sidebar-context";
import { ISpiff } from "@/spiffs/spiffs.d";
import { EnrichedUserWithSpiffs } from "@/user/user";

interface SpiffWithTechnician extends ISpiff {
  technician: EnrichedUserWithSpiffs;
}

interface PayrollPeriodTableProps {
  enrichedUsers: EnrichedUserWithSpiffs[];
  selectedSpiffs: RowSelectionState;
  setSelectedSpiffs: OnChangeFn<RowSelectionState>;
  isLocked: boolean;
  onUndismissSpiff: (spiffId: string) => void;
}

export const PayrollPeriodTable = ({
  enrichedUsers,
  selectedSpiffs,
  setSelectedSpiffs,
  isLocked,
  onUndismissSpiff,
}: PayrollPeriodTableProps) => {
  const { updateSidebar } = useSpiffSidebar();

  const allSpiffs = useMemo(
    () =>
      enrichedUsers
        .flatMap((user) =>
          user.spiffs.map(
            (spiff) =>
              ({
                ...spiff,
                technician: user,
              }) as SpiffWithTechnician,
          ),
        )
        .sort((a, b) => {
          // First, sort by dismissed status (pending first, dismissed last)
          const aDismissed = !!a.dismissed_at;
          const bDismissed = !!b.dismissed_at;

          if (aDismissed !== bDismissed) {
            return aDismissed ? 1 : -1;
          }

          // Within same status, sort by date descending (newest first)
          return sortDateDescending(a.event_date, b.event_date);
        }),
    [enrichedUsers],
  );

  const shouldScroll = allSpiffs.length > 8;

  const handleRowClick = (spiff: SpiffWithTechnician) => {
    updateSidebar({
      isOpen: true,
      section: SPIFF_SIDEBAR_SECTIONS.spiffDetail,
      selectedItemId: spiff.id,
    });
  };

  const columns: ColumnDef<SpiffWithTechnician>[] = useMemo(
    () => [
      SelectionWithUndismissCol<SpiffWithTechnician>(
        onUndismissSpiff,
        isLocked,
      ),
      FlatTechnicianCol<SpiffWithTechnician>(),
      EventCol<SpiffWithTechnician>(),
      SpiffTypeCol<SpiffWithTechnician>(),
      EventDateCol<SpiffWithTechnician>(),
      SpiffPaymentAmountCol<SpiffWithTechnician>(),
    ],
    [isLocked, onUndismissSpiff],
  );

  if (allSpiffs.length === 0) {
    return (
      <Stack className="p-8" align="center">
        <Typography variant="bodyW2" textColor="secondary">
          No spiffs in this period
        </Typography>
      </Stack>
    );
  }

  return (
    <SimpleTable
      columns={columns}
      data={allSpiffs}
      enableRowSelection={!isLocked}
      rowSelection={selectedSpiffs}
      onRowSelectionChange={setSelectedSpiffs}
      onRowClick={handleRowClick}
      getRowId={(row) => row.id}
      getRowVariant={(row) => (row.dismissed_at ? "dismissed" : undefined)}
      scrollAreaClassName="pr-0"
      scrollAreaHeightClassName={shouldScroll ? "h-[600px]" : undefined}
      className="h-full min-h-0 w-full"
      tableClassName="min-w-[1200px]"
    />
  );
};
